// QRLogoUpload.jsx
import React from 'react';
import styled from '@emotion/styled';
import { Upload, X } from 'lucide-react';

const LogoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DropZone = styled.div`
  position: relative;
  background: rgba(255, 255, 255, 0.05);
  border: 2px dashed rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 2rem 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 140px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(124, 58, 237, 0.4);
  }

  &:active {
    transform: scale(0.98);
  }

  ${({ hasLogo }) =>
    hasLogo &&
    `
    border-style: solid;
    background: rgba(255, 255, 255, 0.08);
    min-height: 120px;
  `}
`;

const HiddenInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
`;

const LogoPreview = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`;

const LogoImage = styled.img`
  max-width: 90px;
  max-height: 70px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  background: white;
  padding: 4px;
`;

const LogoFileName = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
`;

const UploadIcon = styled.div`
  width: 40px;
  height: 40px;
  background: rgba(124, 58, 237, 0.2);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7c3aed;
`;

const UploadText = styled.div`
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
`;

const UploadInstructions = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.4;
  max-width: 200px;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.95);
  color: white;
  border: 2px solid white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

  &:hover {
    background: rgba(220, 38, 38, 1);
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const LogoControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
`;

const SizeLabel = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const SizeOptions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const SizeOption = styled.button`
  flex: 1;
  padding: 0.5rem 1rem;
  background: ${({ active }) =>
    active ? 'rgba(124, 58, 237, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid
    ${({ active }) =>
      active ? 'rgba(124, 58, 237, 0.5)' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 8px;
  color: ${({ active }) => (active ? '#ffffff' : 'rgba(255, 255, 255, 0.8)')};
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(124, 58, 237, 0.2);
    border-color: rgba(124, 58, 237, 0.3);
    color: white;
  }
`;

const QRLogoUpload = ({ logoFile, logoSize, onLogoChange, onSizeChange }) => {
  const [fileName, setFileName] = React.useState('');

  // Convert between percentage and size names
  const getSizeName = (percentage) => {
    if (percentage <= 20) return 'small';
    if (percentage <= 30) return 'medium';
    return 'large';
  };

  const getSizePercentage = (sizeName) => {
    switch (sizeName) {
      case 'small':
        return 15;
      case 'medium':
        return 25;
      case 'large':
        return 35;
      default:
        return 25;
    }
  };

  const currentSize = getSizeName(logoSize);

  const handleSizeChange = (sizeName) => {
    onSizeChange(getSizePercentage(sizeName));
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      setFileName(file.name);

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Skip resizing if already small enough
          if (img.width <= 512 && img.height <= 512) {
            onLogoChange(e.target.result);
            return;
          }

          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Calculate optimal size
          const maxSize = 512;
          let { width, height } = img;

          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }

          canvas.width = width;
          canvas.height = height;

          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);

          const optimizedDataUrl = canvas.toDataURL('image/png', 1.0);
          onLogoChange(optimizedDataUrl);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onLogoChange(null);
    setFileName('');
  };

  return (
    <LogoSection>
      <DropZone hasLogo={!!logoFile}>
        {logoFile ? (
          <LogoPreview>
            <RemoveButton onClick={handleRemoveLogo}>
              <X size={14} />
            </RemoveButton>
            <LogoImage src={logoFile} alt="Logo preview" />
            {fileName && <LogoFileName>{fileName}</LogoFileName>}
          </LogoPreview>
        ) : (
          <EmptyState>
            <UploadIcon>
              <Upload size={20} />
            </UploadIcon>
            <div>
              <UploadText>Drop logo here or click to upload</UploadText>
              <UploadInstructions>
                PNG, JPG, SVG up to 5MB
                <br />
                Transparent backgrounds work best
              </UploadInstructions>
            </div>
          </EmptyState>
        )}
        <HiddenInput type="file" accept="image/*" onChange={handleLogoUpload} />
      </DropZone>

      {logoFile && (
        <LogoControls>
          <SizeLabel>Logo Size:</SizeLabel>
          <SizeOptions>
            <SizeOption
              active={currentSize === 'small'}
              onClick={() => handleSizeChange('small')}
            >
              Small
            </SizeOption>
            <SizeOption
              active={currentSize === 'medium'}
              onClick={() => handleSizeChange('medium')}
            >
              Medium
            </SizeOption>
            <SizeOption
              active={currentSize === 'large'}
              onClick={() => handleSizeChange('large')}
            >
              Large
            </SizeOption>
          </SizeOptions>
        </LogoControls>
      )}
    </LogoSection>
  );
};

export default QRLogoUpload;
